import React, { useEffect, useState } from "react";
import { Inbox as InboxIcon, Trash2, Mail, Download, Eye } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import Widget from "@/components/widgets/Widget";
import { api, API, formatApiError, getToken } from "@/lib/api";
import { toast, Toaster } from "sonner";

export default function Inbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null); // full inbox item

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inbox");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Remove this delivery?")) return;
    try {
      await api.delete(`/inbox/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const view = async (id) => {
    try {
      const { data } = await api.get(`/inbox/${id}`);
      setViewing(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const download = async (item, fmt) => {
    try {
      const res = await fetch(`${API}/my-dashboards/${item.dashboard_id}/export/${fmt}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(item.dashboard_name || "report").replace(/\W+/g, "_")}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar title="Inbox" subtitle="Generated reports & scheduled deliveries" />
      <div className="p-6 animate-fade-in" data-testid="inbox-page">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-md border border-dashed border-slate-200 bg-white p-12 text-center">
            <InboxIcon className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">No deliveries yet. Run a report or wait for a scheduled one.</p>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3 text-left">Report</th>
                  <th className="px-5 py-3 text-left">When</th>
                  <th className="px-5 py-3 text-left">Source</th>
                  <th className="px-5 py-3 text-left">Formats</th>
                  <th className="px-5 py-3 text-left">Email recipients</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                    data-testid={`inbox-row-${it.id}`}
                  >
                    <td className="px-5 py-3 font-medium text-navy-600">{it.dashboard_name}</td>
                    <td className="px-5 py-3 text-xs tabular text-slate-500">
                      {new Date(it.generated_at).toLocaleString()}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                          it.delivery_kind === "scheduled"
                            ? "bg-brand-orange/15 text-brand-orange"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {it.delivery_kind}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1">
                        {(it.formats || []).map((f) => (
                          <button
                            key={f}
                            type="button"
                            onClick={() => download(it, f)}
                            data-testid={`download-${it.id}-${f}`}
                            className="flex items-center gap-0.5 rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-slate-600 hover:bg-navy-600 hover:text-white"
                            title={`Download ${f.toUpperCase()}`}
                          >
                            <Download className="h-2.5 w-2.5" />
                            {f}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-500">
                      {(it.email_recipients || []).length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {it.email_recipients.join(", ")}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => view(it.id)}
                          data-testid={`view-${it.id}`}
                          className="rounded-sm p-1.5 text-slate-400 hover:bg-slate-100 hover:text-navy-600"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(it.id)}
                          data-testid={`delete-inbox-${it.id}`}
                          className="rounded-sm p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Snapshot viewer */}
      {viewing && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-navy-600/70 p-4 backdrop-blur-sm"
          data-testid="snapshot-modal"
          onClick={() => setViewing(null)}
        >
          <div
            className="my-12 w-full max-w-6xl rounded-md border border-slate-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-orange">
                  {viewing.delivery_kind} delivery · snapshot
                </div>
                <div className="font-display text-lg font-semibold text-navy-600">
                  {viewing.dashboard_name}
                </div>
              </div>
              <button onClick={() => setViewing(null)} className="rounded-sm p-1 text-slate-400 hover:text-navy-600">
                ✕
              </button>
            </div>
            <div className="space-y-3 p-5">
              <div className="grid grid-cols-12 gap-3">
                {(viewing.snapshot?.widgets || []).map((w) => {
                  const span = Math.max(1, Math.min(w.w || 4, 12));
                  return (
                    <div
                      key={w.instance_id}
                      style={{ gridColumn: `span ${span} / span ${span}`, minHeight: `${(w.h || 1) * 110}px` }}
                    >
                      <Widget widget={w} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
